// ============================================================================
// PROBAR BOT — el simulador dentro del panel (C7-D2).
// Chatea contra el MOTOR REAL y el CATÁLOGO REAL, sin gastar un mensaje de
// WhatsApp. Muestra lo que un vendedor no ve normalmente: qué herramienta usó
// el bot (de dónde sacó el dato) y cuánto costó el turno.
//
// ⭐⭐ T-SIM-16 (31-ago-2026) · AHORA VA CON SESIÓN Y CON NEGOCIO.
// 🔴 Lo medido: el 31-ago esta pantalla, ya publicada, devolvía
// `Failed to execute 'json' on 'Response'` — porque el backend NO EXISTÍA fuera
// del Mac y Cloudflare contestaba un 405 con cuerpo vacío. Al publicar el
// backend hacen falta tres cosas de este lado:
//   1. el `access_token` de la sesión del panel, en `Authorization`;
//   2. el `tenantId` ACTIVO del selector de negocio, en el cuerpo — el servidor
//      NO se lo cree y comprueba la membresía contra la base;
//   3. no llamar siquiera si falta cualquiera de los dos.
// ⚠️ Y toda respuesta se lee como TEXTO antes de intentar el JSON: un cuerpo
// vacío tiene que salir como un aviso legible, nunca como el error del
// navegador que nadie sabe traducir.
// ============================================================================
import { useEffect, useRef, useState } from "react";
import { Bot, CircleDollarSign, Inbox as IconoInbox, Send, Wrench } from "lucide-react";
import { Aviso, Boton, Chip } from "./piezas.tsx";

interface MensajeIA { rol: "system" | "user" | "assistant" | "tool"; contenido: string; [k: string]: unknown }

interface Turno {
  de: "cliente" | "bot";
  texto: string;
  fotos?: string[];
  meta?: { tools: string[]; costoUsd: number; proveedor?: string; transferir?: boolean; degradado?: boolean };
}

const SUGERENCIAS = [
  "Hola, ¿tienes colchones?",
  "¿Cuánto vale el Bassic de 140x190?",
  "¿Dónde quedan ubicados?",
  "Mándame una foto del sofá cama",
];

export function ProbarBot({
  apiUrl = "/api/simular", tenantId = null, obtenerToken, onVerEnInbox,
}: {
  apiUrl?: string;
  /** El negocio ACTIVO del selector. Sin él no se llama al servidor. */
  tenantId?: string | null;
  /**
   * El `access_token` vivo de la sesión. Es una FUNCIÓN y no un string a
   * propósito: el token de Supabase se renueva solo, y uno guardado al montar la
   * pantalla estaría vencido al cabo de una hora — el usuario vería un 401 sin
   * entender por qué. Se pide justo antes de cada envío.
   */
  obtenerToken?: () => Promise<string | null>;
  /** Lo cablea la app que monta el panel (aquí no se enruta: C7-D1). */
  onVerEnInbox?: (conversacionId: string) => void;
}) {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [historial, setHistorial] = useState<MensajeIA[]>([]);
  const [texto, setTexto] = useState("");
  const [pensando, setPensando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Conversación del inbox donde está quedando esta prueba (null = no se guarda).
  const [conversacionId, setConversacionId] = useState<string | null>(null);
  const [avisoDb, setAvisoDb] = useState<string | null>(null);
  const finRef = useRef<HTMLDivElement>(null);

  useEffect(() => { finRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [turnos.length, pensando]);

  // ⛔⛔ AL CAMBIAR DE NEGOCIO SE BORRA LA CHARLA, y no es cosmética: el
  // `historial` viaja al servidor en cada turno. Sin esto, cambiar de negocio en
  // el selector le mandaría al bot del segundo cliente la conversación que se
  // tuvo con el primero — datos de un negocio dentro del prompt de otro.
  useEffect(() => {
    setTurnos([]); setHistorial([]); setConversacionId(null); setAvisoDb(null); setError(null);
  }, [tenantId]);

  const costoTotal = turnos.reduce((s, t) => s + (t.meta?.costoUsd ?? 0), 0);

  async function enviar(t?: string) {
    const msg = (t ?? texto).trim();
    if (!msg || pensando) return;
    setError(null);
    // ⛔⛔ SIN SESIÓN O SIN NEGOCIO NI SE LLAMA. No es solo cortesía con el
    // servidor: llamar sin token produce un 401 que el usuario lee como «el bot
    // está caído», cuando lo que pasa es que su sesión venció.
    const token = obtenerToken ? await obtenerToken() : null;
    if (!token || !tenantId) {
      setError(token
        ? "No hay un negocio seleccionado: elige uno arriba y vuelve a intentar."
        : "Tu sesión no está activa. Vuelve a entrar al panel y prueba otra vez.");
      return;
    }
    setTexto("");
    setTurnos((p) => [...p, { de: "cliente", texto: msg }]);
    setPensando(true);
    try {
      const r = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ texto: msg, historial, tenantId }),
      });
      // ⚠️ TEXTO PRIMERO, JSON DESPUÉS. `r.json()` sobre un cuerpo vacío lanza
      // `Failed to execute 'json' on 'Response'` — literalmente lo que Cristhian
      // vio el 31-ago cuando Cloudflare contestaba el 405 mudo. Un aviso que el
      // usuario no puede interpretar es igual de inútil que ninguno.
      const crudo = await r.text();
      let data: any = {};
      if (crudo) { try { data = JSON.parse(crudo); } catch { data = {}; } }
      if (!r.ok || data.error) {
        throw new Error(data.error ?? (crudo
          ? `HTTP ${r.status}: ${crudo.slice(0, 200)}`
          : `El servidor del simulador contestó ${r.status} sin explicación. ` +
            `Si es un 404 o un 405, la API no está publicada en esa dirección.`));
      }
      const burbujas: string[] = data.burbujas?.length ? data.burbujas : [""];
      setHistorial(data.historial ?? []);
      setConversacionId(data.conversacionId ?? null);
      setAvisoDb(data.avisoDb ?? null);
      setTurnos((p) => [
        ...p,
        ...burbujas.map((b: string, i: number) => ({
          de: "bot" as const, texto: b,
          fotos: i === burbujas.length - 1 ? data.fotos : undefined,
          meta: i === burbujas.length - 1 ? data.meta : undefined,
        })),
      ]);
    } catch (e: any) {
      setError(e?.message ?? "No respondió el simulador. ¿Está corriendo la API en el 5179?");
    } finally {
      setPensando(false);
    }
  }

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-[19px] font-semibold tracking-tight">Probar bot</h1>
          <Chip tono="marca"><Bot size={11} /> motor real</Chip>
          {costoTotal > 0 && (
            <Chip tono="neutro" titulo="Costo real de esta prueba">
              <CircleDollarSign size={11} /> US$ {costoTotal.toFixed(5)}
            </Chip>
          )}
          {conversacionId && onVerEnInbox && (
            <button
              onClick={() => onVerEnInbox(conversacionId)}
              title="Esta prueba quedó guardada como una conversación real"
              /* En móvil sigue la fila de chips; desde sm se va al extremo derecho.
                 Con `ml-auto` siempre, al envolver quedaba solo y descolgado. */
              className="sm:ml-auto inline-flex items-center gap-1.5 text-[12.5px] px-2.5 py-1 rounded-full
                border border-borde bg-superficie text-tinta-2 hover:text-tinta hover:border-marca/40
                active:scale-[0.97] transition cursor-pointer"
            >
              <IconoInbox size={12} /> Ver en el inbox
            </button>
          )}
        </div>
        <p className="text-[13px] text-tinta-2 mt-0.5">
          Habla como si fueras el cliente. Responde con el catálogo y los precios reales, sin gastar un
          mensaje de WhatsApp{conversacionId ? ", y la charla queda en el inbox como una conversación más" : ""}.
        </p>
      </header>

      {/* Tope de ancho: en 1440p/4K un chat a pantalla completa es ilegible. */}
      <div className="flex-1 overflow-y-auto scroll-fino lienzo py-4">
        <div className="max-w-[860px] mx-auto w-full">
        {turnos.length === 0 && (
          <div className="max-w-md mx-auto px-4 text-center">
            <p className="text-sm text-tinta-2 mb-3">Prueba con una de estas:</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGERENCIAS.map((s) => (
                <button key={s} onClick={() => enviar(s)}
                  className="text-[13px] px-3 py-2 rounded-full border border-borde bg-superficie
                    hover:border-marca/40 active:scale-[0.97] transition cursor-pointer">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {turnos.map((t, i) => (
          <div key={i} className={`flex px-3 sm:px-5 ${t.de === "cliente" ? "justify-end" : "justify-start"}`}>
            <div className={`burbuja ${t.de === "cliente" ? "burbuja-out" : "burbuja-in"} entra
              max-w-[85%] sm:max-w-[66%] rounded-2xl px-3 py-2 my-1 shadow-[var(--shadow-tarjeta)]
              ${t.de === "cliente"
                ? "bg-[var(--color-burbuja-out)] rounded-tr-sm"
                : "bg-[var(--color-burbuja-bot)] rounded-tl-sm"}`}
              style={{ ["--cola" as any]: t.de === "cliente" ? "var(--color-burbuja-out)" : "var(--color-burbuja-bot)" }}>
              {t.texto && <p className="text-[14.5px] leading-[1.45] whitespace-pre-wrap break-words">{t.texto}</p>}
              {t.fotos?.map((f) => (
                <img key={f} src={f} alt="Foto del producto enviada por el bot" loading="lazy"
                  className="rounded-lg mt-1.5 max-h-56 w-auto" />
              ))}
              {t.meta && (t.meta.tools?.length || t.meta.transferir) && (
                <div className="flex flex-wrap items-center gap-1 mt-1.5 pt-1.5 border-t border-borde-suave/70">
                  {t.meta.tools?.map((h, j) => (
                    <Chip key={`${h}-${j}`} tono="neutro" titulo="Herramienta que consultó el bot">
                      <Wrench size={9} /> {h}
                    </Chip>
                  ))}
                  {t.meta.transferir && <Chip tono="alerta">pide asesor</Chip>}
                  {t.meta.degradado && <Chip tono="alerta">modo degradado</Chip>}
                </div>
              )}
            </div>
          </div>
        ))}

        {pensando && (
          <div className="flex justify-start px-3 sm:px-5">
            <div className="bg-[var(--color-burbuja-bot)] rounded-2xl rounded-tl-sm px-3.5 py-3 my-1 flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <span key={i} className="h-1.5 w-1.5 rounded-full bg-acento/70 latido"
                  style={{ animationDelay: `${i * 0.16}s` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={finRef} />
        </div>
      </div>

      <footer className="shrink-0 border-t border-borde-suave bg-superficie px-3 sm:px-4 py-2.5
        [&>*]:max-w-[860px] [&>*]:mx-auto space-y-2">
        {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
        {/* ⛔ El texto del aviso sale ENTERO del servidor y aquí NO se le antepone
            nada. Antes esta línea escribía «El bot respondió, pero la charla no se
            está guardando en el inbox:» y el servidor mandaba el `e.message` crudo
            de Postgres. Al dejar de mandar el error crudo (⚪ del fiscal,
            1-sep-2026) el servidor pasó a mandar la frase COMPLETA… y la pantalla
            la repitió: Cristhian vio el aviso diciendo dos veces lo mismo antes de
            llegar al motivo. Una sola verdad, y es la del servidor. */}
        {avisoDb && (
          <Aviso tono="alerta" onCerrar={() => setAvisoDb(null)}>{avisoDb}</Aviso>
        )}
        <div className="flex items-end gap-2">
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") enviar(); }}
            placeholder="Escribe como si fueras el cliente…"
            aria-label="Mensaje de prueba"
            className="flex-1 h-11 px-3.5 rounded-xl bg-elevado border border-borde text-[15px]
              placeholder:text-tinta-2/70 focus:outline-none focus:border-marca/50"
          />
          <Boton tono="marca" onClick={() => enviar()} disabled={!texto.trim() || pensando}
            className="h-11 w-11 !px-0 rounded-xl" aria-label="Enviar">
            <Send size={17} />
          </Boton>
        </div>
      </footer>
    </div>
  );
}
